// 專案的製作（帳號不是全站製作、在某個專案的角色是製作）的「用戶」頁（/users）：
//   管他負責的專案有誰 —— 加動畫師和副手、改角色、移出專案、傳邀請連結（內容就是成員面板那一份）。
//   - 全站製作打開 → 轉去 /admin/users（他有自己的用戶頁）
//   - 不是任何專案的製作 → 帶回自己的頁面，右下角說一聲
//   - 只列他是製作的專案；兩個以上用下拉選（放在網址 ?project=），一個就直接寫專案名字
// 哪些專案是他管的、?project= 怎麼判斷：lib/managedProjects.ts（純判斷，有測試）。
// 導演不能由專案的製作設定：說明文字用 lib/projectMembers.ts 的 PROJECT_ADMIN_DIRECTOR_TEXT（前後台共用，這裡只讀不改）。

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import * as api from '../lib/api';
import type { Project } from '../lib/mockData';
import { MANAGED_PROJECT_PARAM, managedProjects, pickManagedProject } from '../lib/managedProjects';
import { PROJECT_ADMIN_DIRECTOR_TEXT } from '../lib/projectMembers';
import { NotAllowedRedirect, RouteLoading } from '../lib/roleRouting';
import MyProjectsShell from '../lib/MyProjectsShell';
import { ProjectMembersPanel } from '../components/ProjectMembersDialog';

export default function MyUsers() {
  const { user } = useAuth();
  if (!user) return null;  // 外層 RequireAuth 已經擋掉沒登入的
  // 全站製作有自己的用戶頁
  if (user.role === 'admin') return <Navigate to="/admin/users" replace />;
  return <MyUsersBody />;
}

function MyUsersBody() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setError(null);
    setProjects(null);
    api.listProjects()
      .then(list => { if (!cancelled) setProjects(list); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => load(), [load]);

  const managed = useMemo(() => managedProjects(user?.role, projects), [user?.role, projects]);
  const picked = pickManagedProject(managed, params.get(MANAGED_PROJECT_PARAM));

  function choose(projectId: string) {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      next.set(MANAGED_PROJECT_PARAM, projectId);
      return next;
    }, { replace: true });
  }

  if (!user) return null;

  // 讀不到專案清單 → 不知道他管哪些專案：留在外框裡說一聲、可以重試
  if (error) {
    return (
      <MyProjectsShell manager={false} active="users">
        <h1>用戶</h1>
        <div className="my-projects__notice my-projects__notice--error" role="alert">
          <p>讀不到專案清單：{error}</p>
          <button className="btn btn--ghost" onClick={load}>重試</button>
        </div>
      </MyProjectsShell>
    );
  }
  if (!projects) return <RouteLoading />;
  // 不是任何專案的製作 → 帶回自己的頁面
  if (managed.length === 0) {
    return <NotAllowedRedirect text="「用戶」頁只有 PM 可以開，已帶你回自己的頁面" />;
  }

  return (
    <MyProjectsShell manager active="users">
      <h1>用戶</h1>
      <p className="my-projects__sub">
        這裡管你負責的專案有誰：加動畫師和副手、改角色、移出專案、傳邀請連結。{PROJECT_ADMIN_DIRECTOR_TEXT}。
      </p>

      <div className="my-users__project">
        {managed.length > 1 ? (
          <label className="my-users__picker">
            <span>專案</span>
            <select
              value={picked?.id}
              onChange={(e) => choose(e.target.value)}
              aria-label="選專案"
            >
              {managed.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        ) : (
          <p className="my-users__project-name">專案：<strong>{managed[0].name}</strong></p>
        )}
      </div>

      {picked && (
        <div className="my-users__panel">
          {/* 換專案就重新掛一份（成員、搜尋、邀請的狀態不會混到上一個專案） */}
          <ProjectMembersPanel key={picked.id} projectId={picked.id} projectName={picked.name} />
        </div>
      )}
    </MyProjectsShell>
  );
}
